import React, { useEffect, useState } from 'react';
import { fetchProducts, submitBulkOrder } from '../../lib/api';

const BulkOrderForm = ({ partnerId, sevaBalance, bulkOrders = [], onOrdered }) => {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    product_slug: '',
    units: 24,
    redeem_seva: true,
  });
  const [state, setState] = useState({ loading: false, err: '', ok: null });

  useEffect(() => {
    fetchProducts()
      .then((p) => {
        setProducts(p);
        if (!form.product_slug && p.length) {
          setForm((f) => ({ ...f, product_slug: p[0].slug }));
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const product = products.find((p) => p.slug === form.product_slug);
  const unitPrice = product ? product.price_usd * 0.55 : 0;
  const total = unitPrice * form.units;
  const sevaCost = form.redeem_seva ? total : 0;
  const insufficient = form.redeem_seva && sevaCost > sevaBalance;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (insufficient) return;
    setState({ loading: true, err: '', ok: null });
    try {
      const res = await submitBulkOrder(partnerId, form);
      setState({ loading: false, err: '', ok: res });
      if (onOrdered) onOrdered(res);
    } catch (err) {
      setState({
        loading: false,
        ok: null,
        err: err?.response?.data?.detail || 'Could not place order.',
      });
    }
  };

  return (
    <section data-testid="partner-bulk" className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <form
        onSubmit={onSubmit}
        data-testid="bulk-order-form"
        className="lg:col-span-6 p-8 border border-[#C1D7E2] bg-[#F4F8F9]"
      >
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-5">
          Redeem $SEVA for bulk units
        </p>
        <div className="space-y-4">
          <Field label="Product">
            <select
              value={form.product_slug}
              onChange={(e) =>
                setForm((f) => ({ ...f, product_slug: e.target.value }))
              }
              data-testid="bulk-product"
              className="bulk-input"
            >
              {products.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.name} — ${p.price_usd.toFixed(0)} retail
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Units">
              <input
                type="number"
                min={1}
                max={5000}
                value={form.units}
                onChange={(e) =>
                  setForm((f) => ({ ...f, units: Number(e.target.value) }))
                }
                data-testid="bulk-units"
                className="bulk-input"
              />
            </Field>
            <Field label="Unit price (wholesale)">
              <p
                data-testid="bulk-unit-price"
                className="font-mono text-base text-[#7C9CB1] py-2"
              >
                ${unitPrice.toFixed(2)}
              </p>
            </Field>
          </div>

          <label className="flex items-center gap-3 pt-2">
            <input
              type="checkbox"
              checked={form.redeem_seva}
              onChange={(e) =>
                setForm((f) => ({ ...f, redeem_seva: e.target.checked }))
              }
              data-testid="bulk-redeem-seva"
              className="accent-[#7C9CB1]"
            />
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#7C9CB1]">
              Redeem with $SEVA
            </span>
          </label>

          <div
            data-testid="bulk-totals"
            className="font-mono text-xs text-[#7C9CB1] border border-[#C1D7E2] p-4 space-y-2"
          >
            <Row l="Total" v={`$${total.toFixed(2)}`} />
            <Row l="SEVA required" v={`${sevaCost.toFixed(2)}`} />
            <Row
              l="SEVA balance"
              v={Number(sevaBalance || 0).toFixed(2)}
              warn={insufficient}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={state.loading || insufficient}
          data-testid="bulk-submit"
          className="mt-6 bg-[#7C9CB1] text-[#F4F8F9] px-8 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#90B0BF] transition-colors disabled:opacity-50"
        >
          {state.loading ? 'Placing…' : 'Place bulk order'}
        </button>
        {state.err && (
          <p data-testid="bulk-error" className="mt-3 text-sm text-[#7C9CB1]">
            {state.err}
          </p>
        )}
        {state.ok && (
          <div
            data-testid="bulk-success"
            className="mt-4 border border-[#7C9CB1] bg-[#C1D7E2]/40 px-4 py-3"
          >
            <p className="font-serif italic text-[#7C9CB1] text-lg">
              Order placed · {state.ok.units} × {state.ok.product_name}
            </p>
            <p className="mt-1 font-mono text-[11px] tracking-[0.2em] text-[#90B0BF]">
              Total ${state.ok.total_usd.toFixed(2)} ·{' '}
              {state.ok.seva_used > 0
                ? `−${state.ok.seva_used.toFixed(2)} SEVA redeemed`
                : 'Cash invoice'}
            </p>
          </div>
        )}
      </form>

      <div className="lg:col-span-6" data-testid="bulk-history">
        <h3 className="font-serif text-2xl text-[#7C9CB1] font-light mb-5">
          Bulk order ledger
        </h3>
        {bulkOrders.length === 0 ? (
          <p className="font-serif italic text-[#90B0BF] py-6">
            No bulk orders yet.
          </p>
        ) : (
          <ol className="divide-y divide-[#C1D7E2]">
            {bulkOrders.map((o, i) => (
              <li
                key={o.id}
                data-testid={`bulk-row-${i}`}
                className="py-4 flex items-start justify-between gap-4 flex-wrap"
              >
                <div className="min-w-0">
                  <p className="font-serif text-base text-[#7C9CB1]">
                    {o.product_name}
                  </p>
                  <p className="font-mono text-[11px] text-[#90B0BF]">
                    {new Date(o.created_at).toLocaleString()} · {o.units} units
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm text-[#7C9CB1]">
                    ${o.total_usd.toFixed(2)}
                  </p>
                  {o.seva_used > 0 && (
                    <p className="font-mono text-[11px] text-[#90B0BF]">
                      −{o.seva_used.toFixed(2)} SEVA
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <style>{`
        .bulk-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(124,156,177,0.4);
          color: #7C9CB1;
          font-family: 'Outfit', sans-serif;
          font-weight: 300;
          padding: 8px 2px;
          font-size: 14px;
        }
        .bulk-input:focus { outline: none; border-bottom-color: #7C9CB1; }
      `}</style>
    </section>
  );
};

const Row = ({ l, v, warn }) => (
  <div className="flex justify-between gap-3">
    <span className="text-[#90B0BF] tracking-[0.2em] uppercase text-[10px]">{l}</span>
    <span className={warn ? 'text-[#7C9CB1] font-medium' : ''}>
      {warn ? `${v} — insufficient` : v}
    </span>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
      {label}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

export default BulkOrderForm;
